import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import scrapeJobApi from '../services/scrapeJobApi';
import analysisJobApi from '../services/analysisJobApi';
import JobProgress from '../components/JobProgress';
import ScrapeJobResults from '../components/ScrapeJobResults';
import BrowserSessions from '../components/BrowserSessions';
import Loading from '../components/Loading';

const ScrapeJobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [scrapeJob, setScrapeJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedFileTypes, setSelectedFileTypes] = useState({
    'html': false,
    'reduced-level1-html': true,
    'reduced-level2-html': false,
    'screenshot': true
  });
  const [analysisParms, setAnalysisParms] = useState({
    prompt: "Analyze this website and provide insights on user experience, content structure, and potential improvements.",
    modelName: "gemini-1.5-pro",
    temperature: 0.7,
    topP: 0.95
  });

  // Fetch scrape job data on component mount
  useEffect(() => {
    const fetchScrapeJob = async () => {
      try {
        setIsLoading(true);
        const data = await scrapeJobApi.getScrapeJob(jobId);
        setScrapeJob(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching scrape job:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScrapeJob();
    
    // Poll for updates if job is in progress
    const intervalId = setInterval(async () => {
      try {
        const data = await scrapeJobApi.getScrapeJob(jobId);
        setScrapeJob(data);
        
        // Stop polling if job is completed or failed
        if (['completed', 'failed', 'cancelled'].includes(data.status)) {
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Error polling scrape job:', err);
        clearInterval(intervalId);
      }
    }, 5000); // Poll every 5 seconds
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [jobId]);

  // Handle file type selection for analysis
  const handleFileTypeChange = (type) => {
    setSelectedFileTypes({
      ...selectedFileTypes,
      [type]: !selectedFileTypes[type]
    });
  };

  // Handle analysis prompt change
  const handlePromptChange = (e) => {
    setAnalysisParms({
      ...analysisParms,
      prompt: e.target.value
    });
  };

  // Handle model selection
  const handleModelChange = (e) => {
    setAnalysisParms({
      ...analysisParms,
      modelName: e.target.value
    });
  };

  // Create analysis job from scrape job
  const handleCreateAnalysis = async () => {
    try {
      setIsAnalyzing(true);
      
      // Get selected file types for analysis
      const scrapeJobParts = Object.entries(selectedFileTypes)
        .filter(([_, selected]) => selected)
        .map(([fileType, _]) => ({
          scrapeJobId: jobId,
          fileType
        }));
      
      // Create analysis job
      const newAnalysisJob = await analysisJobApi.createAnalysisJob({
        scrapeJobParts,
        prompt: analysisParms.prompt,
        modelName: analysisParms.modelName,
        temperature: analysisParms.temperature,
        topP: analysisParms.topP
      });
      
      // Navigate to the new analysis job
      navigate(`/advanced/analysis-jobs/${newAnalysisJob.jobId}`);
    } catch (err) {
      setError(err.message);
      console.error('Error creating analysis job:', err);
      setIsAnalyzing(false);
    }
  };

  // Cancel scrape job
  const handleCancelJob = async () => {
    if (!window.confirm("Are you sure you want to cancel this scrape job?")) {
      return;
    }
    
    try {
      await scrapeJobApi.cancelScrapeJob(jobId);
      
      // Refresh job data
      const updatedJob = await scrapeJobApi.getScrapeJob(jobId);
      setScrapeJob(updatedJob);
    } catch (err) {
      setError(err.message);
      console.error('Error cancelling scrape job:', err);
    }
  };

  // Format the date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (isLoading) {
    return <Loading message="Loading scrape job details..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">Error: {error}</p>
            <div className="mt-2">
              <Link to="/advanced/scrape-jobs" className="text-sm text-red-700 underline">Back to Scrape Jobs</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!scrapeJob) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">Scrape job not found.</p>
            <div className="mt-2">
              <Link to="/advanced/scrape-jobs" className="text-sm text-yellow-700 underline">Back to Scrape Jobs</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 truncate" title={scrapeJob.targetUrl}>
            {scrapeJob.targetUrl}
          </h1>
          <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              Created: {formatDate(scrapeJob.createdAt)}
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
              Max Links: {scrapeJob.maxLinksToCrawl}
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
              Parallel: {scrapeJob.parallelSessions}
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Pages Successfully Processed: {scrapeJob.pagesProcessed || 0}
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Page Errors: {scrapeJob.pageErrors || 0}
            </div>
            {scrapeJob.completedAt && (
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Completed: {formatDate(scrapeJob.completedAt)}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link to="/scrape-jobs" className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            Back to Scrape Jobs
          </Link>
          {/* {scrapeJob.status === 'pending' || scrapeJob.status === 'processing' ? (
            <button
              onClick={handleCancelJob}
              className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Cancel Job
            </button>
          ) : (
            <button
              onClick={handleCreateAnalysis}
              disabled={isAnalyzing || !scrapeJob.files || scrapeJob.files.length === 0}
              className={`ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                isAnalyzing || !scrapeJob.files || scrapeJob.files.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {isAnalyzing ? 'Creating Analysis...' : 'Create Analysis'}
            </button>
          )} */}
        </div>
      </div>

      {/* Job status and progress */}
      <JobProgress 
        jobId={jobId} 
        jobType="scrapeJob" 
        progressItems={scrapeJob.progressItems || []} 
        isCompleted={['completed', 'failed', 'cancelled'].includes(scrapeJob.status)}
      />
      
      {/* Browser Sessions (only show if job is in progress or has sessions) */}
      {(scrapeJob.browserSessions && scrapeJob.browserSessions.length > 0) && (
        <BrowserSessions 
          browserSessions={scrapeJob.browserSessions} 
          isInProgress={scrapeJob.status === 'pending' || scrapeJob.status === 'processing'}
        />
      )}

      {/* Analysis Options (only show if job is completed successfully) */}
      {/* {scrapeJob.status === 'completed' && scrapeJob.files && scrapeJob.files.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create Analysis</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Content Types to Analyze:
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    id="html-checkbox"
                    name="html"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={selectedFileTypes['html']}
                    onChange={() => handleFileTypeChange('html')}
                  />
                  <label htmlFor="html-checkbox" className="ml-2 block text-sm text-gray-700">
                    Full HTML (Complete web page code)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="reduced-level1-html-checkbox"
                    name="reduced-level1-html"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={selectedFileTypes['reduced-level1-html']}
                    onChange={() => handleFileTypeChange('reduced-level1-html')}
                  />
                  <label htmlFor="reduced-level1-html-checkbox" className="ml-2 block text-sm text-gray-700">
                    Reduced HTML Level 1 (Preserves structure with classes/IDs)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="reduced-level2-html-checkbox"
                    name="reduced-level2-html"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={selectedFileTypes['reduced-level2-html']}
                    onChange={() => handleFileTypeChange('reduced-level2-html')}
                  />
                  <label htmlFor="reduced-level2-html-checkbox" className="ml-2 block text-sm text-gray-700">
                    Reduced HTML Level 2 (Minimal content-only HTML)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="screenshot-checkbox"
                    name="screenshot"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={selectedFileTypes['screenshot']}
                    onChange={() => handleFileTypeChange('screenshot')}
                  />
                  <label htmlFor="screenshot-checkbox" className="ml-2 block text-sm text-gray-700">
                    Screenshots (Visual representation of pages)
                  </label>
                </div>
              </div>
            </div> */}
            
            {/* <div>
              <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
                Analysis Prompt:
              </label>
              <textarea
                id="prompt"
                name="prompt"
                rows="4"
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                value={analysisParms.prompt}
                onChange={handlePromptChange}
              ></textarea>
            </div> */}
            
            {/* Model Selection */}
            {/* <div>
              <label htmlFor="modelName" className="block text-sm font-medium text-gray-700 mb-2">
                AI Model:
              </label>
              <select
                id="modelName"
                name="modelName"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                value={analysisParms.modelName}
                onChange={handleModelChange}
              >
                <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                <option value="claude-3-opus">Claude 3 Opus</option>
                <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                <option value="gpt-4.1">GPT 4.1</option>
              </select>
            </div>
          </div>
           */}
          {/* <div className="mt-4">
            <button
              type="button"
              onClick={handleCreateAnalysis}
              disabled={isAnalyzing || Object.values(selectedFileTypes).every(v => !v)}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                isAnalyzing || Object.values(selectedFileTypes).every(v => !v)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {isAnalyzing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Analysis...
                </>
              ) : (
                'Create Analysis'
              )}
            </button>
          </div> 
        </div>
      )}*/}

      {scrapeJob.files && scrapeJob.files.length > 0 ? (
        <ScrapeJobResults 
          files={scrapeJob.files} 
          browserSessions={scrapeJob.browserSessions || []} 
        />
      ) : scrapeJob.status === 'completed' ? (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                No files were found for this scrape job.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ScrapeJobDetails;